import React, { useState, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiSearch, FiX } from 'react-icons/fi';
import { useBrands } from '../hooks/useBrands';
import { useCategories } from '../hooks/useCategories';
import { useProducts } from '../hooks/useProducts';
import { useLanguage } from '../context/LanguageContext';
import { slugify } from '../utils/slugify';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px 20px;
`;

const Header = styled.div`
  margin-bottom: 40px;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  color: #1f2937;
  margin-bottom: 12px;
  
  @media (max-width: 640px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  max-width: 600px;
  margin: 0 auto;
`;

const SearchContainer = styled.div`
  max-width: 600px;
  margin: 0 auto 32px;
  position: relative;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  background: white;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 16px;
  gap: 12px;
  transition: all 0.3s ease;
  
  &:focus-within {
    border-color: #5efba9;
    box-shadow: 0 0 0 3px rgba(94, 251, 169, 0.1);
  }
`;

const SearchIcon = styled(FiSearch)`
  color: #9ca3af;
  font-size: 20px;
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 16px;
  color: #1f2937;
  background: transparent;
  
  &::placeholder {
    color: #9ca3af;
  }
  
  @media (max-width: 640px) {
    font-size: 14px;
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
  
  &:hover {
    color: #6b7280;
  }
`;

const SearchResults = styled.div`
  margin-top: 16px;
  text-align: center;
  color: #6b7280;
  font-size: 14px;
`;

const BrandsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 24px;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
  }
`;

const BrandCard = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px 24px;
  border: 2px solid #e5e7eb;
  border-radius: 16px;
  background: white;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  text-decoration: none;
  color: inherit;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #5efba9, #34D399);
    transform: scaleX(0);
    transition: transform 0.3s ease;
  }

  &:hover {
    border-color: #5efba9;
    box-shadow: 0 8px 24px rgba(94, 251, 169, 0.2);
    transform: translateY(-4px);
    
    &::before {
      transform: scaleX(1);
    }
  }
  
  @media (max-width: 640px) {
    padding: 24px 16px;
    gap: 12px;
  }
`;

const BrandImageContainer = styled.div`
  width: 100%;
  aspect-ratio: 4 / 3;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.3s ease;
  will-change: transform;
  
  ${BrandCard}:hover & {
    transform: scale(1.05);
  }
`;

const BrandImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: opacity 0.3s ease;
`;

const BrandIcon = styled.div`
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 56px;
  
  @media (max-width: 640px) {
    font-size: 40px;
  }
`;

const BrandInfo = styled.div`
  text-align: center;
  width: 100%;
`;

const BrandName = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 12px 0;
  
  @media (max-width: 640px) {
    font-size: 16px;
    margin: 0 0 10px 0;
  }
`;

const ViewProductsButton = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #5efba9;
  font-size: 14px;
  font-weight: 600;
  margin-top: 8px;
  transition: all 0.3s ease;
  
  ${BrandCard}:hover & {
    color: #34d399;
  }
  
  &::after {
    content: '→';
    font-size: 16px;
    margin-left: 4px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  color: #6b7280;
  font-size: 16px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #6b7280;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

const EmptyStateTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 8px;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  color: #6b7280;
`;

const CategoryBar = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 50%, #ffffff 100%);
  border: 1px solid rgba(94, 251, 169, 0.2);
  box-shadow: 
    0 4px 6px -1px rgba(0, 0, 0, 0.05),
    0 2px 4px -1px rgba(0, 0, 0, 0.03),
    inset 0 1px 0 0 rgba(255, 255, 255, 0.8);
  position: relative;
  margin-bottom: 32px;
  border-radius: 16px;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #5efba9 0%, #34d399 50%, #5efba9 100%);
    background-size: 200% 100%;
    animation: shimmer 3s ease-in-out infinite;
  }
  
  @keyframes shimmer {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }

  @media (max-width: 768px) {
    margin-bottom: 24px;
    border-radius: 12px;
  }
`;

const CategoryContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
  position: relative;

  @media (max-width: 768px) {
    padding: 10px 16px;
    gap: 8px;
  }
`;

const CategoryMenu = styled.nav`
  display: flex;
  align-items: center;
  gap: 8px;
  overflow-x: auto;
  flex: 1;
  padding: 4px 0;
  scroll-behavior: smooth;
  scrollbar-width: none;
  -ms-overflow-style: none;
  
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CategoryButton = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 12px;
  background: ${props => props.active 
    ? 'linear-gradient(135deg, #5efba9 0%, #34d399 100%)' 
    : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'};
  color: ${props => props.active ? '#0f172a' : '#475569'};
  font-size: 14px;
  font-weight: ${props => props.active ? '700' : '600'};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid ${props => props.active ? 'rgba(94, 251, 169, 0.5)' : 'rgba(226, 232, 240, 0.6)'};
  box-shadow: ${props => props.active 
    ? '0 4px 14px rgba(94, 251, 169, 0.35), 0 2px 4px rgba(94, 251, 169, 0.2)' 
    : '0 2px 4px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)'};
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  
  &:hover {
    background: ${props => props.active
    ? 'linear-gradient(135deg, #34d399 0%, #10b981 100%)'
    : 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'};
    transform: translateY(-3px) scale(1.02);
    box-shadow: ${props => props.active
    ? '0 8px 20px rgba(94, 251, 169, 0.4), 0 4px 8px rgba(94, 251, 169, 0.25)'
    : '0 6px 16px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)'};
    border-color: ${props => props.active ? 'rgba(94, 251, 169, 0.8)' : 'rgba(94, 251, 169, 0.4)'};
    color: ${props => props.active ? '#0f172a' : '#1e293b'};
  }
  
  &:active {
    transform: translateY(-1px) scale(0.98);
  }

  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 13px;
    min-height: 36px;
    border-radius: 10px;
  }

  @media (max-width: 480px) {
    padding: 7px 14px;
    font-size: 12px;
    min-height: 34px;
  }
`;

function Brands() {
  const { brands, loading } = useBrands();
  const { categories: allCategories } = useCategories();
  const { products: allProducts } = useProducts();
  const { language, isArabic } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const categoryMenuRef = useRef(null);
  const isBrandActive = (brand) => {
    if (typeof brand?.active === 'boolean') return brand.active;
    if (typeof brand?.is_active === 'boolean') return brand.is_active;
    if (brand?.active === 1 || brand?.active === '1' || brand?.active === 'true') return true;
    if (brand?.is_active === 1 || brand?.is_active === '1' || brand?.is_active === 'true') return true;
    return false;
  };

  // Get categories that have brands with active products (same logic as Home.js)
  const brandCategories = useMemo(() => {
    if (!allCategories.length || !allProducts.length) return [];
    
    // Get all category IDs from active products that have brands
    const categoryIdsWithBrands = [...new Set(
      allProducts
        .filter(product => product.brand_id && product.category_id && product.status === 'active')
        .map(product => product.category_id ? String(product.category_id) : null)
        .filter(Boolean)
    )];
    
    // Filter categories to only include those that have brands with products
    return allCategories.filter(cat => {
      const categoryId = cat.id ? String(cat.id) : null;
      return categoryId && categoryIdsWithBrands.includes(categoryId);
    });
  }, [allCategories, allProducts]);

  // Map categories to display format (same as Home.js)
  const displayCategories = useMemo(() => {
    return brandCategories.map(cat => ({
      id: cat.id,
      name: language === 'ar' ? (cat.name_ar || cat.name_en || cat.name) : (cat.name_en || cat.name),
      slug: slugify(cat.name_en || cat.name),
      img: cat.img
    }));
  }, [brandCategories, language]);

  // Filter brands based on search term and selected category
  const filteredBrands = useMemo(() => {
    // Ensure only active brands are shown
    let filtered = brands.filter(isBrandActive);
    
    // Filter by category if not 'all'
    if (selectedCategory !== 'all') {
      const selectedCategoryId = String(selectedCategory);
      
      // Get brand IDs that have products in the selected category
      const brandIdsInCategory = [...new Set(
        allProducts
          .filter(product => {
            const productCategoryId = product.category_id ? String(product.category_id) : null;
            return productCategoryId === selectedCategoryId && product.status === 'active';
          })
          .map(product => product.brand_id ? String(product.brand_id) : null)
          .filter(Boolean)
      )];
      
      // Filter brands to only include those with products in the selected category
      filtered = brands.filter(brand => {
        const brandId = brand.id ? String(brand.id) : null;
        return brandId && brandIdsInCategory.includes(brandId);
      });
    }
    
    // Filter by search term
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(brand => 
        brand.name?.toLowerCase().includes(searchLower)
      );
    }
    
    return filtered;
  }, [brands, allProducts, selectedCategory, searchTerm]);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          Loading brands...
        </LoadingContainer>
      </Container>
    );
  }

  if (brands.length === 0) {
    return (
      <Container>
        <Header>
          <Title>Our Brands</Title>
          <Subtitle>Discover products from top brands</Subtitle>
        </Header>
        <EmptyState>
          <EmptyStateIcon>🏷️</EmptyStateIcon>
          <EmptyStateTitle>No Brands Available</EmptyStateTitle>
          <EmptyStateText>Brands will appear here once they are added.</EmptyStateText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Our Brands</Title>
        <Subtitle>Explore products from {brands.length} trusted brands</Subtitle>
      </Header>

      {/* Category Tabs */}
      {displayCategories.length > 0 && (
        <CategoryBar>
          <CategoryContent>
            <CategoryMenu ref={categoryMenuRef}>
              <CategoryButton
                active={selectedCategory === 'all'}
                onClick={() => setSelectedCategory('all')}
              >
                {language === 'ar' ? 'الكل' : 'All'}
              </CategoryButton>
              {displayCategories.map((category, index) => (
                <CategoryButton
                  key={category.id || index}
                  active={selectedCategory === category.id}
                  onClick={() => setSelectedCategory(category.id)}
                >
                  {category.name}
                </CategoryButton>
              ))}
            </CategoryMenu>
          </CategoryContent>
        </CategoryBar>
      )}

      <SearchContainer>
        <SearchBar>
          <SearchIcon />
          <SearchInput
            type="text"
            placeholder="Search brands..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <ClearButton
              onClick={() => setSearchTerm('')}
              title="Clear search"
            >
              <FiX size={18} />
            </ClearButton>
          )}
        </SearchBar>
        {searchTerm && (
          <SearchResults>
            Found {filteredBrands.length} {filteredBrands.length === 1 ? 'brand' : 'brands'} matching "{searchTerm}"
          </SearchResults>
        )}
      </SearchContainer>

      {filteredBrands.length === 0 ? (
        <EmptyState>
          <EmptyStateIcon>🔍</EmptyStateIcon>
          <EmptyStateTitle>No Brands Found</EmptyStateTitle>
          <EmptyStateText>
            No brands match "{searchTerm}". Try a different search term.
          </EmptyStateText>
        </EmptyState>
      ) : (
        <BrandsGrid>
          {filteredBrands.map((brand) => (
            <BrandCard key={brand.id} to={`/${slugify(brand.name)}`}>
              <BrandImageContainer>
                {brand.img && brand.img.trim() !== '' ? (
                  <BrandImage 
                    src={brand.img} 
                    alt={brand.name || 'Brand'}
                    onError={(e) => {
                      // Hide image and show fallback icon if image fails to load
                      e.target.style.display = 'none';
                    }}
                  />
                ) : null}
                {(!brand.img || brand.img.trim() === '') && (
                  <BrandIcon>🏷️</BrandIcon>
                )}
              </BrandImageContainer>
              <BrandInfo>
                <BrandName>{brand.name}</BrandName>
                <ViewProductsButton>
                  {isArabic ? 'عرض المنتجات' : 'View Products'}
                </ViewProductsButton>
              </BrandInfo>
            </BrandCard>
          ))}
        </BrandsGrid>
      )}
    </Container>
  );
}

export default Brands;

